import React from 'react';
import { CheckCircle2Icon, CircleDashedIcon, FileTextIcon, PlugZapIcon } from 'lucide-react';
type UrlaSection = {
  id: string;
  number: string;
  label: string;
  description: string;
  state: 'complete' | 'partial' | 'empty';
};
const sections: UrlaSection[] = [{
  id: '1a',
  number: '1a',
  label: 'Personal Information',
  description: 'Name, contact, marital status, dependents, current address',
  state: 'complete'
}, {
  id: '1b',
  number: '1b',
  label: 'Current Employment / Self-Employment & Income',
  description: 'Employer, position, start date, monthly income',
  state: 'partial'
}, {
  id: '2',
  number: '2',
  label: 'Financial Information — Assets & Liabilities',
  description: 'Accounts, gifts, credit cards, other obligations',
  state: 'empty'
}, {
  id: '3',
  number: '3',
  label: 'Financial Information — Real Estate',
  description: 'Property owned, mortgage loans on that property',
  state: 'empty'
}, {
  id: '4',
  number: '4',
  label: 'Loan and Property Information',
  description: 'Loan amount, purpose, property address, occupancy',
  state: 'partial'
}, {
  id: '5',
  number: '5',
  label: 'Declarations',
  description: 'About this property and your money for this loan',
  state: 'empty'
}, {
  id: '6',
  number: '6',
  label: 'Acknowledgements and Agreements',
  description: 'Borrower signature and consent',
  state: 'empty'
}, {
  id: '7',
  number: '7',
  label: 'Demographic Information',
  description: 'Ethnicity, sex, race — government monitoring',
  state: 'empty'
}];
const stateTone = {
  complete: 'text-success',
  partial: 'text-brand',
  empty: 'text-gray-300'
} as const;
const stateLabel = {
  complete: 'Complete',
  partial: 'In progress',
  empty: 'Not started'
} as const;
export function UrlaPanel({
  borrower
}: {
  borrower: string;
}) {
  const complete = sections.filter(section => section.state === 'complete').length;
  const percent = Math.round(complete / sections.length * 100);
  return <div className="space-y-4" data-id="element-719" data-mp="3bxwhushntgfm">
      <section className="rounded-xl border border-dashed border-navy/25 bg-navy-soft/40 px-5 py-5" data-id="element-720" data-mp="3bwuvvsz3v9j4">
        <div className="flex flex-wrap items-start gap-3" data-id="element-721" data-mp="3qtm1ijzdhhh6">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy text-white" data-id="element-722" data-mp="3lw3pgkq26m4y">
            <FileTextIcon className="h-5 w-5" aria-hidden="true" data-id="element-723" data-mp="2y5gdnod0vcd4" />
          </span>
          <div className="min-w-0" data-id="element-724" data-mp="2t7ewxa32jp4t">
            <h2 className="font-display text-[16px] font-semibold text-navy" data-id="element-725" data-mp="2t8hkb816n9fz">
              Uniform Residential Loan Application (Form 1003)
            </h2>
            <p className="mt-1 max-w-2xl text-[12.5px] leading-snug text-gray-600" data-id="element-726" data-mp="335m9lgqbrl10">
              The full URLA for {borrower} renders here. This workspace embeds the existing
              1003 application — the sections below reflect its structure and completion
              state so the loan officer can see progress without leaving the lead.
            </p>
          </div>
          <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-navy ring-1 ring-navy/15" data-id="element-727" data-mp="pwtdzwi7uh98">
            <PlugZapIcon className="h-3 w-3" aria-hidden="true" data-id="element-728" data-mp="g21h6uwgjyfi" />
            Embedded form · placeholder
          </span>
        </div>

        <div className="mt-4" data-id="element-729" data-mp="kx6z7imqosyb">
          <div className="flex items-baseline justify-between" data-id="element-730" data-mp="18oulfn923fu9">
            <p className="text-[12px] font-medium text-ink" data-id="element-731" data-mp="18nb6q131tcul">
              {complete} of {sections.length} sections complete
            </p>
            <p className="text-[12px] font-semibold text-brand" data-id="element-732" data-mp="zp7ll5vz6i3b">{percent}%</p>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label="1003 completion" data-id="element-733" data-mp="13p9mvdxst9i2">
            <div className="h-full rounded-full bg-brand" style={{
            width: `${percent}%`
          }} data-id="element-734" data-mp="8i0lvqgvewjp" />
          </div>
        </div>
      </section>

      <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-735" data-mp="3q93423yhv44">
        {sections.map(section => {
        const Icon = section.state === 'complete' ? CheckCircle2Icon : CircleDashedIcon;
        return <li key={section.id} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3" data-id="element-736" data-mp="3pzthgs2np212">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[11px] font-bold text-gray-600" data-id="element-737" data-mp="mha0ilpfhilk">
                {section.number}
              </span>
              <div className="min-w-0 flex-1" data-id="element-738" data-mp="dhmz3zwp8csi">
                <p className="text-[13px] font-medium text-ink" data-id="element-739" data-mp="dgtzrk02kpke">{section.label}</p>
                <p className="mt-0.5 text-[11.5px] leading-snug text-gray-500" data-id="element-740" data-mp="329pfsq913ccp">
                  {section.description}
                </p>
              </div>
              <span className="flex shrink-0 items-center gap-1.5" data-id="element-741" data-mp="1dpjyg20wfp1g">
                <Icon className={['h-4 w-4', stateTone[section.state]].join(' ')} aria-hidden="true" data-id="element-742" data-mp="1dn7933staced" />
                <span className="hidden text-[11px] text-gray-500 sm:inline" data-id="element-743" data-mp="1mox92y0rejnr">
                  {stateLabel[section.state]}
                </span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}